import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Edit3, Trash2, Save, X, Download, FileDown,
    Upload, FileSpreadsheet, BookOpen, Layers, Link2,
    Unlink, Eye, CheckCircle, XCircle, BarChart2, Search, RefreshCw
} from "lucide-react";

import {
    getAllJobsRequest,
    createJobRequest,
    updateJobRequest,
    deleteJobRequest,
    importJobsExcelRequest,
    exportJobsRequest,
    downloadJobsTemplateRequest,
    clearJobsImportResult,
} from "../../redux/actions/Jobactions";

import {
    fetchJobSkillsRequest,
    createJobSkillRequest,
    updateJobSkillRequest,
    deleteJobSkillRequest,
} from "../../redux/actions/jobSkillActions";

import { fetchSkillsRequest }      from "../../redux/actions/skillsActions";
import { fetchNiveauSkillsRequest } from "../../redux/actions/niveauSkillActions";
import { fetchModesEvaluationRequest } from "../../redux/actions/modeEvaluationActions";
import "./JobManagement.css";
import ScrollToTop from "../../Ui/UiJs/ScrollToTop";

/* ════════════════════════════════════════
   MODAL réutilisable
   Harmonisé avec le standard jm-
════════════════════════════════════════ */
const Modal = ({ open, title, onClose, size = "lg", footer, children }) => {
    if (!open) return null;
    const sizeClass = size === "xl" ? "jm-modal-xl" : "";
    return (
        <div className="jm-overlay" onMouseDown={onClose}>
            <div className={`jm-modal ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="jm-modal-header">
                    <div className="jm-modal-title">{title}</div>
                    <button className="jm-modal-close" type="button" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="jm-modal-body">{children}</div>
                {footer && <div className="jm-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ════════════════════════════════════════
   MODAL DÉTAILS DU POSTE
════════════════════════════════════════ */
const JobDetailsModal = ({ open, job, onClose }) => {
    const dispatch = useDispatch();
    const { jobSkills, loading: jsLoading } = useSelector((s) => s.jobSkill || {});

    useEffect(() => {
        if (open && job?.id) {
            dispatch(fetchJobSkillsRequest(job.id));
        }
    }, [open, job?.id, dispatch]);

    const assocs = useMemo(() => Array.isArray(jobSkills) ? jobSkills : [], [jobSkills]);

    const levelBadgeClass = (n) => {
        if (n <= 1) return "jm-badge-grey";
        if (n <= 2) return "jm-badge-green";
        if (n <= 3) return "jm-badge-orange";
        return "jm-badge-red";
    };

    if (!open || !job) return null;

    return (
        <Modal
            open={open}
            title={
                <>
                    <Eye size={16} style={{ color: "#2a5ca9" }} />
                    Détails du poste
                </>
            }
            onClose={onClose}
            size="xl"
            footer={
                <button className="jm-btn jm-btn-outline" onClick={onClose} type="button">Fermer</button>
            }
        >
            <div className="jm-form-section">
                <h3 className="jm-form-title">Informations du poste</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                    <div className="jm-field">
                        <label>Code</label>
                        <span className="jm-strong">{job.code}</span>
                    </div>
                    <div className="jm-field">
                        <label>Nom</label>
                        <span className="jm-name">{job.name}</span>
                    </div>
                    <div className="jm-field">
                        <label>Statut</label>
                        <span className={`jm-badge ${job.isActive ? 'jm-badge-green' : 'jm-badge-grey'}`}>
                            {job.isActive ? <CheckCircle size={11} className="me-1" /> : <XCircle size={11} className="me-1" />}
                            {job.isActive ? "Actif" : "Inactif"}
                        </span>
                    </div>
                    {job.description && (
                        <div className="jm-field jm-field-full">
                            <label>Description</label>
                            <p style={{ fontSize: '12px', color: '#4b5563', margin: 0 }}>{job.description}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="jm-form-section">
                <h3 className="jm-form-title">Statistiques</h3>
                <div style={{ display: "flex", gap: "12px" }}>
                    <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', flex: 1 }}>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#2a5ca9' }}>{assocs.length}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>compétences requises</div>
                    </div>
                    <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', flex: 1 }}>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#2a5ca9' }}>
                            {assocs.length > 0 ? Math.max(...assocs.map((a) => a.niveau || 0)) : "—"}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>niveau max requis</div>
                    </div>
                </div>
            </div>

            <div className="jm-form-section">
                <h3 className="jm-form-title">Compétences requises</h3>
                <div className="jm-table-wrapper" style={{ margin: 0 }}>
                    <table className="jm-table">
                        <thead>
                            <tr>
                                <th>Compétence</th>
                                <th>Code</th>
                                <th>Niveau requis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jsLoading ? (
                                <tr><td colSpan={3}><div className="jm-loading"><div className="jm-spinner" /><span>Chargement...</span></div></td></tr>
                            ) : assocs.length === 0 ? (
                                <tr><td colSpan={3} className="jm-empty">Aucune compétence associée</td></tr>
                            ) : assocs.map((a) => (
                                <tr key={a.id}>
                                    <td className="jm-name">{a.skillNom}</td>
                                    <td><span className="jm-strong">{a.skillCode}</span></td>
                                    <td>
                                        <span className={`jm-badge ${levelBadgeClass(a.niveau)}`}>
                                            <Layers size={11} className="me-1" />
                                            Niveau {a.niveau} — {a.niveauSkillNom}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════
   MODAL ASSOCIATION
════════════════════════════════════════ */
const JobSkillsModal = ({ open, job, onClose }) => {
    const dispatch = useDispatch();
    const { jobSkills, loading: jsLoading } = useSelector((s) => s.jobSkill || {});
    const { list: skillsList }              = useSelector((s) => s.skills || {});
    const { niveauSkills }                  = useSelector((s) => s.niveauSkill || {});
    const { modesEvaluation }               = useSelector((s) => s.modesEvaluation || {});

    const [skillId, setSkillId] = useState("");
    const [niveauId, setNiveauId] = useState("");
    const [modeId, setModeId] = useState("");
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        if (open && job?.id) {
            dispatch(fetchJobSkillsRequest(job.id));
            dispatch(fetchSkillsRequest());
            dispatch(fetchNiveauSkillsRequest());
            dispatch(fetchModesEvaluationRequest());
        }
    }, [open, job?.id, dispatch]);

    const skills = useMemo(() => Array.isArray(skillsList) ? skillsList : [], [skillsList]);
    const niveaux = useMemo(() => Array.isArray(niveauSkills) ? [...niveauSkills].sort((a, b) => a.niveau - b.niveau) : [], [niveauSkills]);
    const assocs = useMemo(() => Array.isArray(jobSkills) ? jobSkills : [], [jobSkills]);

    const usedSkillIds = useMemo(() => new Set(assocs.filter((a) => a.id !== editingId).map((a) => a.skillId)), [assocs, editingId]);

    const resetForm = () => { setSkillId(""); setNiveauId(""); setModeId(""); setEditingId(null); };
    const handleClose = () => { resetForm(); onClose(); };

    const submitAssoc = () => {
        if (!skillId || !niveauId) { Swal.fire({ icon: "warning", text: "Veuillez choisir une compétence et un niveau.", confirmButtonColor: '#2a5ca9' }); return; }
        const payload = { jobId: job.id, skillId: Number(skillId), niveauSkillId: Number(niveauId), modeEvaluationId: modeId ? Number(modeId) : null };
        if (editingId) dispatch(updateJobSkillRequest(editingId, payload));
        else dispatch(createJobSkillRequest(payload));
        resetForm();
    };

    const confirmRemove = (a) => {
        Swal.fire({ title: "Retirer", text: `Retirer "${a.skillNom}" du poste ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#2a5ca9" })
            .then((r) => { if (r.isConfirmed) dispatch(deleteJobSkillRequest(a.id)); });
    };

    if (!open) return null;

    return (
        <Modal
            open={open}
            title={<><BookOpen size={16} /> Compétences requises : {job?.name}</>}
            onClose={handleClose}
            size="xl"
            footer={<button className="jm-btn jm-btn-outline" onClick={handleClose}>Fermer</button>}
        >
            <div className="jm-form-section">
                <h3 className="jm-form-title">{editingId ? "Modifier l'association" : "Associer une compétence"}</h3>
                <div className="jm-form-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr auto", alignItems: "flex-end" }}>
                    <div className="jm-field">
                        <label>Compétence *</label>
                        <select className="jm-select" value={skillId} onChange={(e) => setSkillId(e.target.value)} disabled={!!editingId}>
                            <option value="">-- choisir --</option>
                            {skills.map((s) => (
                                <option key={s.id} value={s.id} disabled={usedSkillIds.has(s.id)}>{s.nom} ({s.code})</option>
                            ))}
                        </select>
                    </div>
                    <div className="jm-field">
                        <label>Niveau requis *</label>
                        <select className="jm-select" value={niveauId} onChange={(e) => setNiveauId(e.target.value)}>
                            <option value="">-- choisir --</option>
                            {niveaux.map((n) => (
                                <option key={n.id} value={n.id}>Niveau {n.niveau} — {n.nom}</option>
                            ))}
                        </select>
                    </div>
                    <div className="jm-field">
                        <label>Mode d'évaluation</label>
                        <select className="jm-select" value={modeId} onChange={(e) => setModeId(e.target.value)}>
                            <option value="">-- aucun --</option>
                            {(modesEvaluation || []).map((m) => (
                                <option key={m.id} value={m.id}>{m.nom}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex gap-2">
                        {editingId && <button className="jm-btn jm-btn-outline" onClick={resetForm}><X size={14} /></button>}
                        <button className="jm-btn jm-btn-primary" onClick={submitAssoc} disabled={jsLoading}>
                            {editingId ? <Save size={14} /> : <Plus size={14} />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="jm-form-section">
                <h3 className="jm-form-title">Liste des compétences ({assocs.length})</h3>
                <div className="jm-table-wrapper" style={{ margin: 0 }}>
                    <table className="jm-table">
                        <thead>
                            <tr>
                                <th>Compétence</th>
                                <th>Code</th>
                                <th>Niveau</th>
                                <th>Mode</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assocs.map((a) => (
                                <tr key={a.id}>
                                    <td className="jm-name">{a.skillNom}</td>
                                    <td><span className="jm-strong">{a.skillCode}</span></td>
                                    <td><span className={`jm-badge jm-badge-blue`}>Niveau {a.niveau}</span></td>
                                    <td><span className="jm-muted">{a.modeEvaluationNom || "—"}</span></td>
                                    <td className="text-end">
                                        <div className="d-flex justify-content-end gap-2">
                                            <button className="jm-icon-btn" onClick={() => { setEditingId(a.id); setSkillId(a.skillId); setNiveauId(a.niveauSkillId); setModeId(a.modeEvaluationId || ""); }}><Edit3 size={14} /></button>
                                            <button className="jm-icon-btn jm-icon-btn-danger" onClick={() => confirmRemove(a)}><Unlink size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════
   COMPOSANT PRINCIPAL
════════════════════════════════════════ */
const JobManagement = () => {
    const dispatch = useDispatch();
    const { jobs, loading, bulkLoading, importResult } = useSelector((s) => s.jobs || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", description: "", isActive: true });
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add");

    const [skillsJob, setSkillsJob] = useState(null);
    const [skillsOpen, setSkillsOpen] = useState(false);
    const [detailsJob, setDetailsJob] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    useEffect(() => { dispatch(getAllJobsRequest()); }, [dispatch]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return (jobs || []).filter((j) => {
            const mQ = !t || (j.code || "").toLowerCase().includes(t) || (j.name || "").toLowerCase().includes(t) || (j.description || "").toLowerCase().includes(t);
            const mF = filter === "all" ? true : filter === "active" ? !!j.isActive : !j.isActive;
            return mQ && mF;
        });
    }, [jobs, q, filter]);

    const openCreate = () => { setEditing(null); setForm({ code: "", name: "", description: "", isActive: true }); setIsModalOpen(true); };
    const openEdit = (j) => { setEditing(j); setForm({ code: j.code || "", name: j.name || "", description: j.description || "", isActive: j.isActive !== undefined ? j.isActive : true }); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); };

    const submit = (e) => {
        e.preventDefault();
        if (!form.code?.trim() || !form.name?.trim()) { Swal.fire({ icon: "warning", text: "Code et Nom obligatoires.", confirmButtonColor: '#2a5ca9' }); return; }
        const payload = { ...form };
        if (editing?.id) dispatch(updateJobRequest(editing.id, payload));
        else dispatch(createJobRequest(payload));
        closeModal();
    };

    const confirmDelete = (j) => {
        Swal.fire({ title: "Supprimer", text: `Supprimer le poste "${j.name}" ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#2a5ca9" })
            .then((r) => { if (r.isConfirmed) dispatch(deleteJobRequest(j.id)); });
    };

    const runBulk = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel.", confirmButtonColor: '#2a5ca9' }); return; }
        dispatch(importJobsExcelRequest(bulkFile, bulkDryRun, bulkAction));
    };

    return (
        <div className="jm-page">
            <div className="jm-header">
                <div>
                    <h1>Gestion des postes</h1>
                    <p>Gérez et organisez les postes de votre organisation</p>
                </div>
                <div className="jm-header-actions">
                    <button className="jm-btn jm-btn-outline" onClick={() => dispatch(downloadJobsTemplateRequest())} disabled={bulkLoading}>
                        <Download size={14} /> Template
                    </button>
                    <button className="jm-btn jm-btn-outline" onClick={() => dispatch(exportJobsRequest())} disabled={bulkLoading}>
                        <FileDown size={14} /> Exporter
                    </button>
                    <button className="jm-btn jm-btn-primary" onClick={() => setBulkOpen(true)}>
                        <Upload size={14} /> Ajout massif
                    </button>
                    <button className="jm-btn jm-btn-primary" onClick={openCreate}>
                        <Plus size={14} /> Ajouter un nouveau poste
                    </button>
                </div>
            </div>

            <div className="jm-toolbar">
                <div className="jm-search-wrapper">
                    <Search className="jm-search-icon" size={14} />
                    <input className="jm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un poste..." />
                </div>
                <div className="jm-filter-tabs">
                    {[{ k: "all", l: "Tous" }, { k: "active", l: "Actifs" }, { k: "inactive", l: "Inactifs" }].map((f) => (
                        <button key={f.k} className={`jm-filter-tab ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>{f.l}</button>
                    ))}
                </div>
                <div className="jm-count">{filtered.length} poste{filtered.length > 1 ? "s" : ""}</div>
            </div>

            <div className="jm-table-wrapper">
                <table className="jm-table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Nom du poste</th>
                            <th>Statut</th>
                            <th>Date de création</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5}><div className="jm-loading"><div className="jm-spinner" /><span>Chargement...</span></div></td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="jm-empty">Aucun poste trouvé</td></tr>
                        ) : filtered.map((j) => (
                            <tr key={j.id}>
                                <td><span className="jm-strong">{j.code}</span></td>
                                <td className="jm-name">{j.name}</td>
                                <td>
                                    <span className={`jm-badge ${j.isActive ? 'jm-badge-green' : 'jm-badge-grey'}`}>
                                        {j.isActive ? "Actif" : "Inactif"}
                                    </span>
                                </td>
                                <td className="jm-muted">{j.createdAt ? new Date(j.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                                <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                        {/*<button className="jm-icon-btn" title="Détails" onClick={() => { setDetailsJob(j); setDetailsOpen(true); }}><Eye size={16} /></button>*/}
                                        {/*<button className="jm-icon-btn" title="Compétences" onClick={() => { setSkillsJob(j); setSkillsOpen(true); }}><BookOpen size={16} /></button>*/}
                                        <button className="jm-icon-btn" title="Modifier" onClick={() => openEdit(j)}><Edit3 size={16} /></button>
                                        <button className="jm-icon-btn jm-icon-btn-danger" title="Supprimer" onClick={() => confirmDelete(j)}><Trash2 size={16} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MODALS */}
            <Modal open={isModalOpen} title={editing ? "Modifier le poste" : "Ajouter un poste"} onClose={closeModal} footer={
                <>
                    <button className="jm-btn jm-btn-outline" onClick={closeModal}>Annuler</button>
                    <button className="jm-btn jm-btn-primary" onClick={submit}><Save size={14} /> {editing ? "Enregistrer" : "Créer"}</button>
                </>
            }>
                <div className="jm-form-section">
                    <div className="jm-form-grid">
                        <div className="jm-field">
                            <label>Code *</label>
                            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="DEV-001" />
                        </div>
                        <div className="jm-field">
                            <label>Nom du poste *</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Développeur" />
                        </div>
                        <div className="jm-field jm-field-full">
                            <label>Description</label>
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
                        </div>
                        <div className="jm-field">
                            <label className="jm-check-label">
                                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                                Poste actif
                            </label>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal open={bulkOpen} title="Ajout massif" onClose={() => setBulkOpen(false)} footer={
                <button className="jm-btn jm-btn-primary" onClick={runBulk} disabled={bulkLoading}>Exécuter</button>
            }>
                <div className="jm-form-section">
                    <div className="jm-form-grid">
                        <div className="jm-field">
                            <label>Action</label>
                            <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                <option value="add">Ajouter</option>
                                <option value="edit">Modifier</option>
                            </select>
                        </div>
                        <div className="jm-field">
                            <label className="jm-check-label">
                                <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} />
                                Prévisualisation avant l'import
                            </label>
                        </div>
                        <div className="jm-field jm-field-full">
                            <label>Fichier</label>
                            <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                        </div>
                    </div>
                </div>
            </Modal>

            <JobDetailsModal open={detailsOpen} job={detailsJob} onClose={() => setDetailsOpen(false)} />
            <JobSkillsModal open={skillsOpen} job={skillsJob} onClose={() => setSkillsOpen(false)} />
            <ScrollToTop />
        </div>
    );
};

export default JobManagement;